import {
  ProvinceAndStateNameMappings,
  countryCodeFromProvinceOrState,
  ProvinceOrStateCode,
  provinceOrStateCodeFromString,
} from 'utils/province_or_state';
import { fetchSearchPredictionsV1 } from 'data/search/predictions/fetcher';
import { AutocompletePredictionPredictionCategory } from 'utils/google-maps/types';
import { CountryCode, CountryCodeList } from 'types/countries';
import type { ExtractorOutput } from './types';
import type { MatchSpan, ParsedSearchFilters } from '../types';

const COUNTRY_NAME_PATTERNS: ReadonlyArray<{ pattern: RegExp; country: CountryCode }> = [
  { pattern: /\b(canada|canadian)\b/i, country: CountryCodeList.CANADA },
  {
    pattern: /\b(usa|u\.s\.a\.?|u\.s\.|united\s+states|america|american)\b/i,
    country: CountryCodeList.UNITED_STATES,
  },
];

interface LocationSignal {
  countryCode: CountryCode;
  provinceCode?: ProvinceOrStateCode;
}

interface ResolvedLocation {
  city?: string;
  neighbourhood?: string;
  province?: string;
  country?: string;
}

const REGEX_SPECIAL_CHARS = /[.*+?^${}()|[\]\\]/g;

function escapeRegex(value: string): string {
  return value.replace(REGEX_SPECIAL_CHARS, '\\$&');
}

/**
 * Detect an explicit country anchor. Predictions API is only called when one
 * of these is present — bare city names fall through to the AI.
 *
 * Precedence: "City, ST" > full province/state name > country word.
 */
function detectLocationSignal(query: string): LocationSignal | null {
  const commaCodeMatch = query.match(/,\s*([A-Za-z]{2})\b/);
  if (commaCodeMatch) {
    const provinceCode = provinceOrStateCodeFromString(commaCodeMatch[1]);
    if (provinceCode) {
      return {
        provinceCode,
        countryCode: countryCodeFromProvinceOrState(provinceCode),
      };
    }
  }

  for (const [name, code] of Object.entries(ProvinceAndStateNameMappings)) {
    const pattern = new RegExp(`\\b${name.replace(/\s+/g, '\\s+')}\\b`, 'i');
    if (pattern.test(query)) {
      return {
        provinceCode: code,
        countryCode: countryCodeFromProvinceOrState(code),
      };
    }
  }

  for (const { pattern, country } of COUNTRY_NAME_PATTERNS) {
    if (pattern.test(query)) {
      return { countryCode: country };
    }
  }

  return null;
}

/**
 * Strip filter-related tokens so the leftover is location-shaped for the
 * predictions API. Includes aliases used by this module (e.g. `bd`).
 */
function stripNonLocationTokens(query: string): string {
  let s = query;
  s = s.replace(/\$\s*\d+(?:[.,]\d+)?\s*[km]?\b/gi, ' ');
  s = s.replace(/\b\d+(?:[.,]\d+)?\s*[km]\b/gi, ' ');
  s = s.replace(
    /\b\d+\s*\+?\s*(?:bedrooms?|beds?|bd|br|bathrooms?|baths?|ba|parking\s+spaces?|parking\s+spots?|car\s+garages?)\b/gi,
    ' '
  );
  s = s.replace(/\b\d+(?:\s*(?:to|-)\s*\d+)?\s*(?:sq\s*ft|sqft|square\s+(?:feet|foot)|sf)\b/gi, ' ');
  s = s.replace(
    /\b(?:homes?|houses?|detached|single[\s-]family|bungalows?|condos?|apartments?|condominiums?|townhouses?|townhomes?|row\s+houses?|farms?|farmhouses?|acreages?|land|lots?|vacant\s+land|commercial|office|retail|industrial|real\s+estate|propert(?:y|ies)|listings?|places?)\b/gi,
    ' '
  );
  s = s.replace(
    /\b(?:garage|pool|swimming\s+pool|fireplaces?|waterfront|lakefront|oceanfront|beachfront|open\s+house|coming\s+soon)\b/gi,
    ' '
  );
  s = s.replace(
    /\b(?:under|over|below|above|less\s+than|more\s+than|between|budget|max(?:imum)?|min(?:imum)?|up\s+to|at\s+least|from|exactly)\b/gi,
    ' '
  );
  s = s.replace(
    /\b(?:for\s+rent|rentals?|to\s+lease|to\s+rent|for\s+sale|to\s+buy|to\s+purchase|recently\s+sold|sold|available|pending|expired|cheapest|newest|oldest)\b/gi,
    ' '
  );
  s = s.replace(/(?<!,\s*)\b(?:in|on|or|me)\b/gi, ' ');
  s = s.replace(
    /\b(?:at|near|around|with|the|a|an|of|by|for|please|show|find|i|want|need|looking|some|any|and|to)\b/gi,
    ' '
  );
  s = s.replace(/[!?;]+/g, ' ');
  s = s.replace(/\s+/g, ' ').trim();
  s = s.replace(/^,+|,+$/g, '').trim();
  return s;
}

function isJustProvinceName(candidate: string, expected: ProvinceOrStateCode): boolean {
  const normalized = candidate.toLowerCase().trim();
  for (const [name, code] of Object.entries(ProvinceAndStateNameMappings)) {
    if (code === expected && name.toLowerCase() === normalized) return true;
  }
  return false;
}

function parsePredictionDescription(
  description: string,
  label: AutocompletePredictionPredictionCategory | string | null
): ResolvedLocation | null {
  const parts = description
    .split(',')
    .map(p => p.trim())
    .filter(Boolean);
  if (parts.length === 0) return null;

  const lastPart = parts[parts.length - 1];
  const provinceCode = provinceOrStateCodeFromString(lastPart);
  if (!provinceCode) return null;

  const base: ResolvedLocation = {
    province: provinceCode.toUpperCase(),
    country: countryCodeFromProvinceOrState(provinceCode),
  };

  if (parts.length === 1 || label === AutocompletePredictionPredictionCategory.PROVINCE) {
    return base;
  }

  if (label === AutocompletePredictionPredictionCategory.NEIGHBORHOOD && parts.length >= 3) {
    return { ...base, neighbourhood: parts[0], city: parts[1] };
  }

  return { ...base, city: parts[0] };
}

function pushTermSpan(
  spans: MatchSpan[],
  query: string,
  term: string,
  field: string,
  value: unknown,
  ruleId: string
): void {
  if (!term) return;
  const pattern = new RegExp(`\\b${escapeRegex(term).replace(/\s+/g, '\\s+')}\\b`, 'i');
  const match = pattern.exec(query);
  if (!match || match.index === undefined) return;
  spans.push({
    start: match.index,
    end: match.index + match[0].length,
    field,
    raw: query.slice(match.index, match.index + match[0].length),
    value,
    confidence: 0.9,
    ruleId,
  });
}

/**
 * Record spans for resolved location terms so residual does not treat them
 * as unparsed leftover.
 */
function buildLocationSpans(query: string, location: ResolvedLocation): MatchSpan[] {
  const spans: MatchSpan[] = [];

  if (location.neighbourhood) {
    pushTermSpan(spans, query, location.neighbourhood, 'neighbourhood', location.neighbourhood, 'location.neighbourhood');
  }
  if (location.city) {
    pushTermSpan(spans, query, location.city, 'city', location.city, 'location.city');
  }
  if (location.province) {
    pushTermSpan(spans, query, location.province, 'province', location.province, 'location.province-code');
    for (const [name, code] of Object.entries(ProvinceAndStateNameMappings)) {
      if (code.toLowerCase() === location.province.toLowerCase()) {
        pushTermSpan(spans, query, name, 'province', location.province, 'location.province-name');
        break;
      }
    }
  }
  if (location.country) {
    for (const { pattern, country } of COUNTRY_NAME_PATTERNS) {
      if (country !== location.country) continue;
      const flags = pattern.flags.includes('g') ? pattern.flags : `${pattern.flags}g`;
      const globalPattern = new RegExp(pattern.source, flags);
      const match = globalPattern.exec(query);
      if (match && match.index !== undefined) {
        spans.push({
          start: match.index,
          end: match.index + match[0].length,
          field: 'country',
          raw: query.slice(match.index, match.index + match[0].length),
          value: location.country,
          confidence: 0.9,
          ruleId: 'location.country',
        });
      }
    }
  }

  // Also span the ", ST" disambiguator when present.
  const commaCode = query.match(/,\s*([A-Za-z]{2})\b/);
  if (commaCode && commaCode.index !== undefined && location.province) {
    const code = commaCode[1];
    if (code.toUpperCase() === location.province.toUpperCase()) {
      spans.push({
        start: commaCode.index,
        end: commaCode.index + commaCode[0].length,
        field: 'province',
        raw: commaCode[0],
        value: location.province,
        confidence: 0.95,
        ruleId: 'location.comma-province',
      });
    }
  }

  return spans;
}

/**
 * Location extraction via Go-Search predictions (ported from the legacy
 * parseNaturalLanguageQueryIntoFilters). Gated by `locationIdentification`.
 */
export async function extractLocation(query: string): Promise<ExtractorOutput> {
  const signal = detectLocationSignal(query);
  if (!signal) return { partial: {}, spans: [] };

  const buildFallback = (): ResolvedLocation => {
    const fallback: ResolvedLocation = { country: signal.countryCode };
    if (signal.provinceCode) fallback.province = signal.provinceCode.toUpperCase();
    return fallback;
  };

  const candidate = stripNonLocationTokens(query);
  let location: ResolvedLocation;

  if (!candidate || !signal.provinceCode || isJustProvinceName(candidate, signal.provinceCode)) {
    location = buildFallback();
  } else {
    try {
      // V1 gets place text only: "Toronto, ON bbq" → "Toronto" (drop ", ST" and anything after).
      const cityMatch = candidate.match(/^(.*?),\s*[A-Za-z]{2}\b/);
      const placeQuery = (cityMatch ? cityMatch[1] : candidate).trim();
      if (!placeQuery) {
        location = buildFallback();
      } else {
        const response = await fetchSearchPredictionsV1({
          query: placeQuery,
          country: signal.countryCode,
        });

        const locationPrediction = response?.data?.find(p => p.attributes.group === 'locations');
        if (locationPrediction) {
          const parsed = parsePredictionDescription(
            locationPrediction.attributes.description,
            locationPrediction.attributes.label
          );
          location = parsed ?? buildFallback();
        } else {
          location = buildFallback();
        }
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('extractLocation: predictions API failed', error);
      location = buildFallback();
    }
  }

  const partial: Partial<ParsedSearchFilters> = { ...location };
  const spans = buildLocationSpans(query, location);
  return { partial, spans };
}
